<template>
	<resource-list-item :to="$spoferanUrl(`/events/${props.event._key}/participations/${props.participation._key}`)"
						divided
						centered
						:image="props.withoutImage ? null : participation.image"
						:image-width="64"
						:title="participation.display_name + (participation.team ? ` | ${participation.team.display_name}` : '')"
						:subtitle="participation.participationClass?.name">

		<template v-if="participation.starter_number || participation.private" #title_suffix>
			<chip v-if="participation.starter_number" x-small class="ml-8">{{ `#${participation.starter_number}` }}</chip>
			<chip v-if="participation.private" x-small color="warning" class="ml-8">
				{{ t('label.anonymous') }}
			</chip>
		</template>

		<template #right>
			<Icon :icon="icons.next" color="gray-400"/>
		</template>

		<template #below>
			<div class="flex flex-column justify-between" :class="{'md:flex-row md:items-center': !props.forceMobileLayout}">
				<div v-if="participation.ranks?.length || rankValues.length || participation.status?.value !== ParticipationStatus.Ranked" class="flex overflow-x-auto overflow-y-hidden border-box px-16 lg:px-24 mb--8 pb-16 pt-4" :class="{'md:pt-0': !props.forceMobileLayout}">
					<chip v-if="participation.status && ((!rankValues.length && !participation.ranks?.length) || participation.status.label !== ParticipationStatus.Ranked)" :color="participation.status.color" small class="mr-4">
						{{ participation.status.title }}
					</chip>
					<template v-if="participation.ranks?.length">
						<chip v-for="(rank, rankKey) in participation.ranks" class="mr-4" small
							  :to="$spoferanUrl(`/events/${props.event._key}/participations/${props.participation.id}/results`)"
							  :key="rankKey"
							  :color="getColorForRank(rank.rank)">
							<div class="flex flex-column items-center justify-center h-full">
								<span class="leading-none text-4xs font-semi-bold" :class="{'text-text-color': !getColorForRank(rank.rank)}">{{ rank.rank }}. {{ t('label.rank') }}</span>
								<span class="leading-none text-5xs sm:text-4xs">{{ rank.name }}</span>
							</div>
						</chip>
					</template>
					<chip v-for="rankValue in rankValues" :key="rankValue.metric"
						  :to="$spoferanUrl(`/events/${props.event._key}/participations/${props.participation.id}/results`)"
						  class="mr-4" small>
						<div class="flex flex-column items-center justify-center h-full">
							<span class="leading-none text-4xs font-semi-bold text-text-color">{{ parseRankMetricValue(rankValue.metric, rankValue.value) }}</span>
							<span class="leading-none text-5xs sm:text-4xs">{{ rankValue.title }}</span>
						</div>
					</chip>
				</div>
				<span v-else></span>

				<btn-group v-if="!props.withoutInteraction" class="justify-end" text>
					<like-form :action="`/v1/events/${props.event.id}/participations/${props.participation.id}/likes`"
							   :count="props.participation.likes_count"
							   :likes="props.participation.likes"
							   :liked="props.participation.likes.some(like => like.id === $auth.id)"
							   :max="10"
							   @show="$emit('show-likes')"
							   ref="likeForm"/>

					<tooltip bottom :text="t('label.comments')">
						<btn @click="$emit('show-comments')" :disabled="props.readonly" text large>
							<Icon small force-size :icon="icons.comment"/>
							<span>{{ props.participation.comments_count }}</span>
						</btn>
					</tooltip>
				</btn-group>
			</div>
		</template>

	</resource-list-item>
</template>

<script setup lang="ts">
import {parseRankMetricValue, ParticipationStatus, RankMetric} from "@spoferan/spoferan-ts-core";
import type {EventPreview} from "@spoferan/spoferan-ts-core";
import {nextIcon} from "../../../config";
import {useColorHelpers} from "../../../composables/useColorHelpers";
import {mdiCommentMultiple} from "@mdi/js";
import {useI18n, useNuxtApp} from "#imports";

const props = defineProps<{
	event: EventPreview,
	participation: object,

	// Whether the bottom content row shall be wrapped even when on desktop devices
	forceMobileLayout?: boolean,

	// Whether the comments and likes sections shall be disabled
	withoutInteraction?: boolean,

	// Whether no participation image shall be displayed
	withoutImage?: boolean,
}>();

const {t} = useI18n();
const {$spoferanUrl, $auth} = useNuxtApp();

const icons = {
	comment: mdiCommentMultiple,
	next: nextIcon,
};

const {getColorForRank} = useColorHelpers();

const rankValues = [
	{metric: RankMetric.Time, value: props.participation.time, title: t('enum.rank_metric.time.title')},
	{metric: RankMetric.Distance, value: props.participation.distance, title: t('enum.rank_metric.distance.title')},
	{metric: RankMetric.Rounds, value: props.participation.rounds, title: t('enum.rank_metric.rounds.title')},
	{metric: RankMetric.Points, value: props.participation.points, title: t('enum.rank_metric.points.title')},
].filter(value => !!value.value);

</script>